import { useEffect, useState } from "react";
import { Alert, Button, Typography } from "@mui/material";
import { MaterialReactTable, type MRT_ColumnDef } from "material-react-table";
import { MRT_Localization_DE } from "material-react-table/locales/de";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { useTranslation } from "react-i18next";
import { api } from "@/helper/api";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import MailPreferencesDialog from "@/components/mailTemplate/mailPreferencesDialog";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";

type Preference = { subject: string; name: string; enabled: boolean };

export default function TemplateMailPreferences({
  traceId,
}: {
  traceId: string;
}) {
  const { t, i18n } = useTranslation();
  const { handleError, errorModal } = useErrorModal();
  const [rows, setRows] = useState<Preference[]>([]);
  const [configurable, setConfigurable] = useState(true);
  const [subjectId, setSubjectId] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let disposed = false;
    setRows([]);
    setSubjectId(null);
    setConfigurable(true);
    api.genericApiRequest({
      method: "get",
      entity: "mail/preferences",
      shouldNotImpersonate: true,
      parametersToRender: { template_trace_id: traceId },
      successHandler: (response: {
        data: { results: Preference[]; configurable: boolean };
      }) => {
        if (!disposed) {
          setRows(response.data.results);
          setConfigurable(response.data.configurable);
        }
      },
      failHandler: (error: unknown) => {
        if (!disposed) handleError(error);
      },
    });
    return () => {
      disposed = true;
    };
  }, [traceId, revision, handleError]);
  const selectSubject = (option: { value: string } | null) => {
    if (option) setSubjectId(option.value);
  };
  const columns: MRT_ColumnDef<Preference>[] = [
    { accessorKey: "name", header: t("name") },
    {
      accessorKey: "enabled",
      header: t("mail_preferences_automatic"),
      Cell: ({ row }) => t(row.original.enabled ? "yes" : "no"),
    },
    {
      id: "edit",
      header: t("edit"),
      Cell: ({ row }) => (
        <Button onClick={() => setSubjectId(row.original.subject)}>
          {t("edit")}
        </Button>
      ),
    },
  ];
  if (!configurable) {
    return (
      <>
        <Alert severity="info">{t("mail_preferences_access_exception")}</Alert>
        {errorModal}
      </>
    );
  }
  return (
    <>
      <Typography variant="h6">{t("mail_preferences_title")}</Typography>
      <Typography>{t("mail_preferences_local_settings")}</Typography>
      <DropDownSubjectSearch
        subdomain="admin"
        queryParameter="companies"
        name="mail-company"
        placeholder={t("company")}
        onChange={selectSubject}
      />
      <DropDownSubjectSearch
        subdomain="admin"
        queryParameter="users"
        name="mail-user"
        placeholder={t("user")}
        onChange={selectSubject}
      />
      <MaterialReactTable
        columns={columns}
        data={rows}
        localization={
          i18n.language.startsWith("en")
            ? MRT_Localization_EN
            : MRT_Localization_DE
        }
        initialState={{ density: "compact" }}
      />
      {subjectId && (
        <MailPreferencesDialog
          key={subjectId}
          subjectId={subjectId}
          templateTraceId={traceId}
          onClose={() => {
            setSubjectId(null);
            setRevision((value) => value + 1);
          }}
        />
      )}
      {errorModal}
    </>
  );
}
